/**
 * Visual PDF-extractor builder.
 *
 * Renders an uploaded invoice PDF in the browser (react-pdf / pdf.js) with a
 * selectable text layer, so an administrator can drag across a label directly
 * on the document and turn it into a `pdf_extractor` rule — the PDF analogue
 * of column mappings, without writing regexes by hand. The selected text
 * becomes the rule's `label`; the destination field is chosen from the
 * existing invoice fields or created fresh.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ChevronLeft,
  ChevronRight,
  FileUp,
  MousePointerClick,
  Save,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import {
  createPdfExtractor,
  listPdfExtractors,
  updatePdfExtractor,
} from "@/services/api/invoiceDocuments";
import type { PdfExtractor } from "@/services/api/types";

// Vite rewrites this asset URL so pdf.js loads its worker as a real web worker.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const PAGE_WIDTH = 720;

/** Distinct colours assigned to each mapped field's highlight rectangle. */
const FIELD_COLORS = [
  "#16a34a",
  "#2563eb",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#ca8a04",
];

/** Common invoice labels → the destination field they almost certainly mean. */
const FIELD_HINTS: Record<string, string> = {
  "invoice number": "invoice_number",
  "invoice no": "invoice_number",
  "invoice no.": "invoice_number",
  "invoice": "invoice_number",
  "invoice date": "invoice_date",
  "date": "invoice_date",
  "total": "amount",
  "amount": "amount",
  "amount due": "amount",
  "grand total": "amount",
  "invoice total": "amount",
  "supplier": "supplier_name",
  "billed by": "supplier_name",
  "load": "load_ref",
  "load ref": "load_ref",
  "load reference": "load_ref",
  "job": "load_ref",
  "job ref": "load_ref",
};

/** Dialog that turns a selected label into a `pdf_extractor` rule. */
function MapSelectionDialog({
  text,
  onClose,
}: {
  text: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const extractors = useQuery({
    queryKey: ["pdf-extractors", "invoice"],
    queryFn: () => listPdfExtractors("invoice"),
  });

  const hint = FIELD_HINTS[text.trim().toLowerCase()] ?? "";
  const hinted = extractors.data?.some((item) => item.destination_field === hint)
    ? hint
    : "";
  const [mode, setMode] = useState<"existing" | "new">(hinted ? "existing" : "new");
  const [field, setField] = useState(hinted);
  const [newField, setNewField] = useState("");
  const [label, setLabel] = useState(text.trim());

  const fields = extractors.data ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const destination = (mode === "existing" ? field : newField.trim()).trim();
      if (!destination) {
        throw new Error("Choose a destination field");
      }
      const existing = fields.find((item) => item.destination_field === destination);
      if (existing) {
        return updatePdfExtractor(existing.id, {
          destination_field: existing.destination_field,
          label,
          pattern: existing.pattern,
          is_required: existing.is_required,
        });
      }
      return createPdfExtractor({
        import_type: "invoice",
        destination_field: destination,
        label,
        is_required: false,
      });
    },
    onSuccess: () => {
      toast.success(`Mapped “${label}” to a field`);
      void queryClient.invalidateQueries({ queryKey: ["pdf-extractors", "invoice"] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const canSave = mode === "existing" ? Boolean(field) : Boolean(newField.trim());

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Map selection to a field</DialogTitle>
          <DialogDescription>
            The selected text becomes the <em>label</em> the extractor looks for on the invoice
            (e.g. “Invoice Number” for “Invoice Number: INV-001”).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Selected label</Label>
            <Input value={label} onChange={(event) => setLabel(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Destination field</Label>
            <Select
              value={mode === "existing" ? field : "__new__"}
              onValueChange={(value) => {
                if (value === "__new__") {
                  setMode("new");
                } else {
                  setMode("existing");
                  setField(value);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a field…" />
              </SelectTrigger>
              <SelectContent>
                {fields.map((item) => (
                  <SelectItem key={item.destination_field} value={item.destination_field}>
                    {item.destination_field}
                    {item.is_required ? " *" : ""}
                  </SelectItem>
                ))}
                <SelectItem value="__new__">New field…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "new" ? (
            <div className="space-y-1.5">
              <Label className="text-xs">New field name</Label>
              <Input
                value={newField}
                placeholder="e.g. vat_amount"
                onChange={(event) => setNewField(event.target.value)}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save.mutate()} disabled={!canSave || save.isPending}>
            <Save className="h-4 w-4" />
            Save mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Renders a sample invoice and lets the admin map labels by selecting text. */
export function PdfMappingBuilder() {
  const [file, setFile] = useState<File | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selection, setSelection] = useState<{
    text: string;
    left: number;
    top: number;
  } | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  const extractors = useQuery({
    queryKey: ["pdf-extractors", "invoice"],
    queryFn: () => listPdfExtractors("invoice"),
  });
  const labelled = useMemo(
    () => (extractors.data ?? []).filter((item) => item.label),
    [extractors.data],
  );

  // Draw a colour-coded rectangle over every mapped label on the rendered page
  // so the admin can see at a glance what is already extracted — on load, after
  // switching pages, and immediately after saving a new mapping (the query
  // invalidation refetches extractors, which re-runs this effect). A
  // MutationObserver re-applies whenever pdf.js adds the real text spans (no
  // render loop — the signature only tracks actual text spans, not our own
  // highlight boxes).
  useEffect(() => {
    const container = pageRef.current;
    if (!container || !file) return;

    const textSignature = (): string => {
      const textLayer = container.querySelector(".textLayer");
      if (!textLayer) return "";
      return Array.from(textLayer.querySelectorAll("span"))
        .filter((span) => !span.closest(".pdf-map-highlight"))
        .map(
          (span) =>
            `${span.offsetLeft},${span.offsetTop},${span.offsetWidth},${span.offsetHeight},${span.textContent}`,
        )
        .join("|");
    };

    let signature = "";
    let frame = 0;
    const apply = () => {
      const next = textSignature();
      if (!next || next === signature) return;
      signature = next;

      const textLayer = container.querySelector(".textLayer");
      if (!textLayer) return;
      container.querySelectorAll(".pdf-map-highlight").forEach((node) => node.remove());
      const spans = Array.from(textLayer.querySelectorAll("span"));
      labelled.forEach((item, index) => {
        const label = item.label?.toLowerCase() ?? "";
        if (!label) return;
        const span = spans.find((node) => {
          const text = (node.textContent ?? "").trim().toLowerCase();
          return text.startsWith(label) || text.includes(label);
        });
        if (!span || span.offsetWidth === 0) return;
        const color = FIELD_COLORS[index % FIELD_COLORS.length];
        const box = document.createElement("div");
        box.className = "pdf-map-highlight";
        box.style.position = "absolute";
        box.style.left = `${span.offsetLeft}px`;
        box.style.top = `${span.offsetTop}px`;
        box.style.width = `${span.offsetWidth}px`;
        box.style.height = `${span.offsetHeight}px`;
        box.style.boxSizing = "border-box";
        box.style.border = `2px solid ${color}`;
        box.style.background = `${color}26`;
        box.style.pointerEvents = "none";
        box.style.zIndex = "3";
        const chip = document.createElement("span");
        chip.textContent = item.destination_field;
        chip.style.position = "absolute";
        chip.style.top = "0";
        chip.style.left = "0";
        chip.style.fontSize = "9px";
        chip.style.lineHeight = "12px";
        chip.style.padding = "0 3px";
        chip.style.color = "#fff";
        chip.style.background = color;
        chip.style.borderRadius = "0 0 3px 0";
        chip.style.whiteSpace = "nowrap";
        chip.style.pointerEvents = "none";
        box.appendChild(chip);
        textLayer.appendChild(box);
      });
    };

    apply();
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    });
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [labelled, file, pageNumber]);

  const handleMouseUp = useCallback(() => {
    const selected = window.getSelection();
    const text = selected?.toString().trim() ?? "";
    if (!text || !selected || selected.rangeCount === 0) {
      setSelection(null);
      return;
    }
    const container = pageRef.current;
    if (!container || !container.contains(selected.getRangeAt(0).commonAncestorContainer)) {
      setSelection(null);
      return;
    }
    const rect = selected.getRangeAt(0).getBoundingClientRect();
    setSelection({ text, left: rect.left, top: Math.max(rect.top - 44, 4) });
  }, []);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-muted bg-muted/30 p-4 text-sm text-muted-foreground">
        Upload a sample invoice and <strong>drag across a label on the document</strong> (for
        example “Invoice Number”) — then click <strong>Map selection</strong> to turn it into an
        extractor field. Fields already mapped are shown below; you can also add or tune them by
        hand.
      </div>

      <PdfExtractorBadges />

      {!file ? (
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed p-8 text-sm text-muted-foreground transition-colors hover:bg-muted/40">
          <FileUp className="h-6 w-6" />
          <span>Choose a sample invoice PDF</span>
          <input
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(event) => {
              const next = event.target.files?.[0] ?? null;
              setFile(next);
              setNumPages(0);
              setPageNumber(1);
              event.target.value = "";
            }}
          />
        </label>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{file.name}</span>
            {numPages > 0 ? (
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((value) => Math.max(1, value - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {pageNumber} of {numPages}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={pageNumber >= numPages}
                  onClick={() => setPageNumber((value) => Math.min(numPages, value + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setFile(null)}>
                  Change file
                </Button>
              </div>
            ) : null}
          </div>

          <div
            ref={pageRef}
            className="relative w-fit rounded-md border bg-white shadow-sm"
            onMouseUp={handleMouseUp}
          >
            <Document file={file} onLoadSuccess={({ numPages: count }) => setNumPages(count)}>
              <Page
                pageNumber={pageNumber}
                width={PAGE_WIDTH}
                renderTextLayer
                renderAnnotationLayer={false}
              />
            </Document>
            <p className="pointer-events-none absolute bottom-1 right-2 z-10 text-[11px] text-slate-400">
              Drag across a label, then click “Map selection”
            </p>
          </div>

          {labelled.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {labelled.map((item, index) => (
                <span
                  key={item.destination_field}
                  className="inline-flex items-center gap-1.5"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm border border-black/10"
                    style={{
                      background: FIELD_COLORS[index % FIELD_COLORS.length],
                    }}
                  />
                  {item.destination_field}
                </span>
              ))}
            </div>
          ) : null}

          {selection ? (
            <Button
              className="fixed z-50 shadow-lg"
              style={{ left: selection.left, top: selection.top }}
              size="sm"
              onClick={() => setMapOpen(true)}
            >
              <MousePointerClick className="h-4 w-4" />
              Map selection
            </Button>
          ) : null}
        </div>
      )}

      {mapOpen && selection ? (
        <MapSelectionDialog
          text={selection.text}
          onClose={() => {
            setMapOpen(false);
            setSelection(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Small read-only badge row of the currently configured invoice fields. */
export function PdfExtractorBadges({ className }: { className?: string }) {
  const extractors = useQuery({
    queryKey: ["pdf-extractors", "invoice"],
    queryFn: () => listPdfExtractors("invoice"),
  });
  const fields = (extractors.data ?? []) as PdfExtractor[];
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {fields.length === 0 ? (
        <span className="text-xs text-muted-foreground">No fields mapped yet.</span>
      ) : (
        fields.map((item) => (
          <Badge key={item.destination_field} variant="outline">
            {item.destination_field}
            {item.label ? ` ← “${item.label}”` : ""}
          </Badge>
        ))
      )}
    </div>
  );
}
